import { useState, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Globe,
  Mic,
  Star,
  CheckCircle,
  ArrowRightLeft,
  Loader2,
  Save,
  X,
  Play,
  Square,
  Volume2,
  Search,
  Filter,
  Check,
  Phone,
  PhoneOff,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/api/client'
import {
  agentPoolApi,
  type AgentProfile,
  type CreateAgentProfileRequest,
  type UpdateAgentProfileRequest,
  type ConfigureAgentNumberRequest,
} from '@/api/endpoints/agentPool'
import { voicesApi, type Voice } from '@/api/endpoints/voices'
import { cn } from '@/lib/utils'

// Language options
const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
]

// Gender options
const GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'neutral', label: 'Neutral' },
]

// Voice provider options
const VOICE_PROVIDER_OPTIONS = [
  { value: 'bland', label: 'Bland AI' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
]

// Default form state for new agent
const defaultAgentForm: CreateAgentProfileRequest = {
  agent_key: '',
  name: '',
  email: '',
  gender: 'female',
  title: 'Sales Representative',
  company: 'Riviera Finance',
  voice_id: '',
  voice_name: '',
  voice_provider: 'bland',
  primary_language: 'en',
  primary_language_name: 'English',
  supported_languages: [],
  is_primary: false,
  is_outbound_default: false,
  display_order: 0,
  is_active: true,
  transfer_phone_number: '',
  transfer_greeting: '',
}

export function AgentPool() {
  const queryClient = useQueryClient()
  const [selectedAgent, setSelectedAgent] = useState<AgentProfile | null>(null)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showPhoneDialog, setShowPhoneDialog] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [configureBland, setConfigureBland] = useState(true)
  const [agentForm, setAgentForm] = useState<CreateAgentProfileRequest>(defaultAgentForm)
  const [isPlayingPreview, setIsPlayingPreview] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [voiceSearchQuery, setVoiceSearchQuery] = useState('')
  const [voiceGenderFilter, setVoiceGenderFilter] = useState<'all' | 'male' | 'female'>('all')
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Fetch pools with agents
  const { data: poolsData, isLoading, refetch } = useQuery({
    queryKey: ['agent-pools'],
    queryFn: () => agentPoolApi.getPools(true).then(res => res.data),
    staleTime: 30000,
  })

  // Fetch transfer stats
  const { data: statsData } = useQuery({
    queryKey: ['agent-pool-stats'],
    queryFn: () => agentPoolApi.getTransferStats().then(res => res.data),
    staleTime: 60000,
  })

  // Fetch available voices
  const { data: voicesData } = useQuery({
    queryKey: ['voices'],
    queryFn: () => voicesApi.getAll().then(res => res.data),
    staleTime: 300000, // 5 minutes
  })

  // Filter voices based on search and gender
  const filteredVoices = useMemo(() => {
    if (!voicesData?.voices) return []

    return voicesData.voices.filter((voice: Voice) => {
      // Search filter
      const searchMatch = !voiceSearchQuery ||
        voice.name?.toLowerCase().includes(voiceSearchQuery.toLowerCase()) ||
        voice.description?.toLowerCase().includes(voiceSearchQuery.toLowerCase()) ||
        voice.tags?.some(t => t?.toLowerCase().includes(voiceSearchQuery.toLowerCase()))

      // Gender filter (infer from metadata)
      const voiceText = `${voice.name || ''} ${voice.description || ''} ${voice.tags?.join(' ') || ''}`.toLowerCase()
      let genderMatch = true
      if (voiceGenderFilter === 'female') {
        genderMatch = voiceText.includes('female')
      } else if (voiceGenderFilter === 'male') {
        genderMatch = voiceText.includes('male') && !voiceText.includes('female')
      }

      return searchMatch && genderMatch
    })
  }, [voicesData?.voices, voiceSearchQuery, voiceGenderFilter])

  // Infer gender from voice
  const inferVoiceGender = (voice: Voice): string | null => {
    const text = `${voice.name || ''} ${voice.description || ''} ${voice.tags?.join(' ') || ''}`.toLowerCase()
    if (text.includes('female')) return 'Female'
    if (text.includes('male')) return 'Male'
    return null
  }

  // Voice preview function
  const handleVoicePreview = async (voiceId: string) => {
    if (!voiceId) {
      toast({ title: 'No voice selected', variant: 'destructive' })
      return
    }

    // Stop current playback if any
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
      setIsPlayingPreview(false)
    }

    setPreviewLoading(true)
    try {
      const previewText = agentForm.transfer_greeting ||
        `Hello, this is ${agentForm.name || 'your AI assistant'} from ${agentForm.company || 'Riviera Finance'}. How can I help you today?`

      const response = await voicesApi.preview(voiceId, previewText)

      if (response.data.success && response.data.audio_data) {
        const audio = new Audio(`data:${response.data.content_type};base64,${response.data.audio_data}`)
        audioRef.current = audio

        audio.onended = () => {
          setIsPlayingPreview(false)
          audioRef.current = null
        }

        audio.onerror = () => {
          setIsPlayingPreview(false)
          audioRef.current = null
          toast({ title: 'Failed to play audio', variant: 'destructive' })
        }

        await audio.play()
        setIsPlayingPreview(true)
      } else {
        toast({ title: 'Failed to generate voice preview', variant: 'destructive' })
      }
    } catch (error) {
      toast({ title: 'Voice preview failed', description: getErrorMessage(error), variant: 'destructive' })
    } finally {
      setPreviewLoading(false)
    }
  }

  // Stop voice preview
  const stopVoicePreview = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
      setIsPlayingPreview(false)
    }
  }

  // Create agent mutation
  const createAgentMutation = useMutation({
    mutationFn: (data: CreateAgentProfileRequest) => agentPoolApi.createAgent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-pools'] })
      setShowAddDialog(false)
      setAgentForm(defaultAgentForm)
      toast({ title: 'Agent created successfully', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to create agent', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  // Update agent mutation
  const updateAgentMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAgentProfileRequest }) =>
      agentPoolApi.updateAgent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-pools'] })
      setShowEditDialog(false)
      setSelectedAgent(null)
      toast({ title: 'Agent updated successfully', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to update agent', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  // Delete agent mutation
  const deleteAgentMutation = useMutation({
    mutationFn: (id: number) => agentPoolApi.deleteAgent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-pools'] })
      setShowDeleteDialog(false)
      setSelectedAgent(null)
      toast({ title: 'Agent deleted successfully', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to delete agent', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  // Configure phone number mutation
  const configurePhoneMutation = useMutation({
    mutationFn: ({ agentKey, data }: { agentKey: string; data: ConfigureAgentNumberRequest }) =>
      agentPoolApi.configureAgentNumber(agentKey, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['agent-pools'] })
      setShowPhoneDialog(false)
      setSelectedAgent(null)
      setPhoneNumber('')
      if (response.data.bland_configured) {
        toast({ title: 'Phone number configured', description: `${response.data.phone_number} assigned and Bland.ai configured`, variant: 'success' })
      } else {
        toast({ title: 'Phone number assigned', description: response.data.message, variant: 'success' })
      }
    },
    onError: (error) => {
      toast({ title: 'Failed to configure phone', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  // Remove phone number mutation
  const removePhoneMutation = useMutation({
    mutationFn: (agentKey: string) => agentPoolApi.removeAgentNumber(agentKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-pools'] })
      toast({ title: 'Phone number removed', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to remove phone', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  // Toggle agent active status
  const toggleAgentStatus = async (agent: AgentProfile) => {
    updateAgentMutation.mutate({
      id: agent.id,
      data: { is_active: !agent.is_active }
    })
  }

  // Open phone configuration dialog
  const handleConfigurePhone = (agent: AgentProfile) => {
    setSelectedAgent(agent)
    setPhoneNumber(agent.assigned_phone_number || '')
    setConfigureBland(true)
    setShowPhoneDialog(true)
  }

  // Submit phone configuration
  const handleSavePhone = () => {
    if (!selectedAgent || !phoneNumber.trim()) return
    configurePhoneMutation.mutate({
      agentKey: selectedAgent.agent_key,
      data: {
        phone_number: phoneNumber.trim(),
        configure_bland: configureBland
      }
    })
  }

  // Open edit dialog
  const handleEditAgent = (agent: AgentProfile) => {
    setSelectedAgent(agent)
    setAgentForm({
      agent_key: agent.agent_key,
      name: agent.name,
      email: agent.email || '',
      gender: agent.gender,
      title: agent.title,
      company: agent.company,
      voice_id: agent.voice_id || '',
      voice_name: agent.voice_name || '',
      voice_provider: agent.voice_provider,
      primary_language: agent.primary_language,
      primary_language_name: agent.primary_language_name || '',
      supported_languages: agent.supported_languages || [],
      is_primary: agent.is_primary,
      is_outbound_default: agent.is_outbound_default,
      display_order: agent.display_order,
      is_active: agent.is_active,
      transfer_phone_number: agent.transfer_phone_number || '',
      transfer_greeting: agent.transfer_greeting || '',
    })
    setShowEditDialog(true)
  }

  // Handle form submission
  const handleSaveAgent = () => {
    if (selectedAgent) {
      updateAgentMutation.mutate({ id: selectedAgent.id, data: agentForm })
    } else {
      createAgentMutation.mutate(agentForm)
    }
  }

  // Get all agents from pools
  const allAgents = poolsData?.pools?.flatMap(pool => pool.agents) || []
  const activePool = poolsData?.pools?.find(p => p.is_active)

  // Get language display
  const getLanguageName = (code: string) => {
    return LANGUAGE_OPTIONS.find(l => l.code === code)?.name || code
  }

  // Get language flag emoji
  const getLanguageFlag = (code: string) => {
    const flags: Record<string, string> = {
      en: '🇺🇸',
      es: '🇪🇸',
      fr: '🇫🇷',
      pt: '🇧🇷',
      de: '🇩🇪',
      it: '🇮🇹',
      zh: '🇨🇳',
      ja: '🇯🇵',
      ko: '🇰🇷',
    }
    return flags[code] || '🌐'
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agent Pool</h1>
          <p className="text-muted-foreground">
            Manage AI agents and language-specific routing
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => {
            setAgentForm(defaultAgentForm)
            setSelectedAgent(null)
            setShowAddDialog(true)
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Agent
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allAgents.length}</p>
                <p className="text-sm text-muted-foreground">Total Agents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allAgents.filter(a => a.is_active).length}</p>
                <p className="text-sm text-muted-foreground">Active Agents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Globe className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {new Set(allAgents.map(a => a.primary_language)).size}
                </p>
                <p className="text-sm text-muted-foreground">Languages</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <ArrowRightLeft className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statsData?.total_transfers || 0}</p>
                <p className="text-sm text-muted-foreground">Total Transfers</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pool Info Card */}
      {activePool && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {activePool.name}
              <Badge className="ml-2 bg-green-100 text-green-800">Active</Badge>
            </CardTitle>
            <CardDescription>{activePool.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span>Language Detection: {activePool.language_detection_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                <span>Auto Transfer: {activePool.auto_transfer_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Agents</CardTitle>
          <CardDescription>
            Configure AI agents for outbound calls and language-specific routing
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : allAgents.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No agents configured</h3>
              <p className="text-muted-foreground mb-4">
                Add your first AI agent to get started
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Agent
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Voice</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allAgents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold",
                          agent.gender === 'female' ? 'bg-pink-500' :
                          agent.gender === 'male' ? 'bg-blue-500' : 'bg-gray-500'
                        )}>
                          {agent.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium">{agent.name}</p>
                          <p className="text-sm text-muted-foreground">{agent.title}</p>
                          <p className="text-xs text-muted-foreground font-mono">{agent.agent_key}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getLanguageFlag(agent.primary_language)}</span>
                        <div>
                          <p className="font-medium">{agent.primary_language_name || getLanguageName(agent.primary_language)}</p>
                          {agent.supported_languages && agent.supported_languages.length > 1 && (
                            <p className="text-xs text-muted-foreground">
                              +{agent.supported_languages.length - 1} more
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mic className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm">{agent.voice_name || 'Default'}</p>
                          <p className="text-xs text-muted-foreground">{agent.voice_provider}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {agent.assigned_phone_number ? (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-green-600" />
                          <div>
                            <p className="text-sm font-mono">{agent.assigned_phone_number}</p>
                            <p className="text-xs text-green-600">Configured</p>
                          </div>
                        </div>
                      ) : agent.is_primary ? (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-blue-700">Main Inbound Line</p>
                            <p className="text-xs text-blue-600">Primary agent</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <PhoneOff className="h-4 w-4" />
                          <span className="text-sm">Not configured</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {agent.is_primary && (
                          <Badge className="bg-yellow-100 text-yellow-800 w-fit">
                            <Star className="h-3 w-3 mr-1" />
                            Primary
                          </Badge>
                        )}
                        {agent.is_outbound_default && (
                          <Badge className="bg-blue-100 text-blue-800 w-fit">
                            Outbound Default
                          </Badge>
                        )}
                        {!agent.is_primary && !agent.is_outbound_default && (
                          <span className="text-sm text-muted-foreground">Standard</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={agent.is_active}
                        onCheckedChange={() => toggleAgentStatus(agent)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditAgent(agent)}
                          title="Edit agent"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleConfigurePhone(agent)}
                          title="Configure phone number"
                          className={agent.assigned_phone_number ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : ''}
                        >
                          <Phone className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedAgent(agent)
                            setShowDeleteDialog(true)
                          }}
                          title="Delete agent"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Transfer Stats */}
      {statsData && statsData.total_transfers > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Transfer Statistics</CardTitle>
            <CardDescription>Language-based call transfer metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground">Successful Transfers</p>
                <p className="text-2xl font-bold text-green-600">{statsData.successful_transfers}</p>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground">Failed Transfers</p>
                <p className="text-2xl font-bold text-red-600">{statsData.failed_transfers}</p>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground">By Language</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {Object.entries(statsData.by_language).map(([lang, count]) => (
                    <Badge key={lang} variant="outline">
                      {getLanguageFlag(lang)} {count}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Agent Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false)
          setShowEditDialog(false)
          setSelectedAgent(null)
          setAgentForm(defaultAgentForm)
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedAgent ? 'Edit Agent' : 'Add New Agent'}
            </DialogTitle>
            <DialogDescription>
              {selectedAgent
                ? 'Update the agent profile settings'
                : 'Configure a new AI agent for the pool'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="voice">Voice & Language</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="agent_key">Agent Key *</Label>
                  <Input
                    id="agent_key"
                    placeholder="e.g., sarah_en"
                    value={agentForm.agent_key}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, agent_key: e.target.value }))}
                    disabled={!!selectedAgent}
                  />
                  <p className="text-xs text-muted-foreground">Unique identifier (cannot be changed)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Sarah Thompson"
                    value={agentForm.name}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="agent@rivierafinance.com"
                    value={agentForm.email || ''}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    value={agentForm.gender}
                    onValueChange={(v) => setAgentForm(prev => ({ ...prev, gender: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="Sales Representative"
                    value={agentForm.title}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    placeholder="Riviera Finance"
                    value={agentForm.company}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, company: e.target.value }))}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="voice" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_language">Primary Language *</Label>
                  <Select
                    value={agentForm.primary_language}
                    onValueChange={(v) => {
                      const lang = LANGUAGE_OPTIONS.find(l => l.code === v)
                      setAgentForm(prev => ({
                        ...prev,
                        primary_language: v,
                        primary_language_name: lang?.name || v
                      }))
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {getLanguageFlag(lang.code)} {lang.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="voice_provider">Voice Provider</Label>
                  <Select
                    value={agentForm.voice_provider}
                    onValueChange={(v) => setAgentForm(prev => ({ ...prev, voice_provider: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VOICE_PROVIDER_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Voice Selection - Improved UI */}
              <div className="space-y-3">
                <Label>Voice Selection</Label>

                {/* Search and Filter Row */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search voices..."
                      value={voiceSearchQuery}
                      onChange={(e) => setVoiceSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={voiceGenderFilter} onValueChange={(v) => setVoiceGenderFilter(v as 'all' | 'male' | 'female')}>
                    <SelectTrigger className="w-[120px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Voice Grid */}
                <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                  {filteredVoices.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <Volume2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No voices found</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {filteredVoices.map((voice: Voice) => {
                        const isSelected = agentForm.voice_id === voice.id
                        const voiceGender = inferVoiceGender(voice)

                        return (
                          <div
                            key={voice.id}
                            className={cn(
                              'flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all',
                              'hover:bg-muted/50 border',
                              isSelected ? 'bg-primary/10 border-primary' : 'border-transparent'
                            )}
                            onClick={() => {
                              setAgentForm(prev => ({
                                ...prev,
                                voice_id: voice.id,
                                voice_name: voice.name || voice.id
                              }))
                            }}
                          >
                            <div className={cn(
                              'p-1.5 rounded-md',
                              voiceGender === 'Female' ? 'bg-pink-100' :
                              voiceGender === 'Male' ? 'bg-blue-100' : 'bg-gray-100'
                            )}>
                              <Mic className={cn(
                                'h-3 w-3',
                                voiceGender === 'Female' ? 'text-pink-600' :
                                voiceGender === 'Male' ? 'text-blue-600' : 'text-gray-600'
                              )} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{voice.name}</p>
                              {voiceGender && (
                                <p className="text-xs text-muted-foreground">{voiceGender}</p>
                              )}
                            </div>
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary flex-shrink-0" />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  {filteredVoices.length} voices available • Click to select
                </p>
              </div>

              {/* Selected Voice Preview */}
              {agentForm.voice_id && (
                <div className="p-3 rounded-lg border bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-md bg-primary/10">
                        <Mic className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{agentForm.voice_name || agentForm.voice_id}</p>
                        <p className="text-xs text-muted-foreground">
                          {agentForm.voice_provider} • {agentForm.voice_id.slice(0, 12)}...
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant={isPlayingPreview ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => isPlayingPreview ? stopVoicePreview() : handleVoicePreview(agentForm.voice_id || '')}
                      disabled={previewLoading}
                    >
                      {previewLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : isPlayingPreview ? (
                        <Square className="h-4 w-4 mr-1" />
                      ) : (
                        <Play className="h-4 w-4 mr-1" />
                      )}
                      {isPlayingPreview ? 'Stop' : 'Preview'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Manual voice ID input for custom voices */}
              <details className="pt-2 border-t">
                <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                  Advanced: Enter Voice ID manually
                </summary>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <Input
                    placeholder="Voice ID (e.g., maya)"
                    value={agentForm.voice_id || ''}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, voice_id: e.target.value }))}
                  />
                  <Input
                    placeholder="Display name"
                    value={agentForm.voice_name || ''}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, voice_name: e.target.value }))}
                  />
                </div>
              </details>

              <div className="space-y-2">
                <Label htmlFor="transfer_greeting">Transfer Greeting</Label>
                <Textarea
                  id="transfer_greeting"
                  placeholder="Greeting when receiving a transferred call..."
                  value={agentForm.transfer_greeting || ''}
                  onChange={(e) => setAgentForm(prev => ({ ...prev, transfer_greeting: e.target.value }))}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  What the agent says when receiving a transferred call (also used for voice preview)
                </p>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="transfer_phone_number">Transfer Phone Number</Label>
                  <Input
                    id="transfer_phone_number"
                    placeholder="+1234567890"
                    value={agentForm.transfer_phone_number || ''}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, transfer_phone_number: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">For human escalations</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="display_order">Display Order</Label>
                  <Input
                    id="display_order"
                    type="number"
                    value={agentForm.display_order}
                    onChange={(e) => setAgentForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Primary Agent</Label>
                    <p className="text-sm text-muted-foreground">Handles all inbound calls</p>
                  </div>
                  <Switch
                    checked={agentForm.is_primary}
                    onCheckedChange={(checked) => setAgentForm(prev => ({ ...prev, is_primary: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Outbound Default</Label>
                    <p className="text-sm text-muted-foreground">Default agent for outbound when no language preference</p>
                  </div>
                  <Switch
                    checked={agentForm.is_outbound_default}
                    onCheckedChange={(checked) => setAgentForm(prev => ({ ...prev, is_outbound_default: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Active</Label>
                    <p className="text-sm text-muted-foreground">Agent is available for calls</p>
                  </div>
                  <Switch
                    checked={agentForm.is_active}
                    onCheckedChange={(checked) => setAgentForm(prev => ({ ...prev, is_active: checked }))}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false)
              setShowEditDialog(false)
              setSelectedAgent(null)
            }}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveAgent}
              disabled={createAgentMutation.isPending || updateAgentMutation.isPending || !agentForm.agent_key || !agentForm.name}
            >
              {(createAgentMutation.isPending || updateAgentMutation.isPending) ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {selectedAgent ? 'Save Changes' : 'Create Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedAgent?.name}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedAgent && deleteAgentMutation.mutate(selectedAgent.id)}
              disabled={deleteAgentMutation.isPending}
            >
              {deleteAgentMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phone Configuration Dialog */}
      <Dialog open={showPhoneDialog} onOpenChange={(open) => {
        if (!open) {
          setShowPhoneDialog(false)
          setSelectedAgent(null)
          setPhoneNumber('')
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Configure Phone Number
            </DialogTitle>
            <DialogDescription>
              Assign a Bland.ai inbound number to <strong>{selectedAgent?.name}</strong> for language-based call routing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Status */}
            {selectedAgent?.assigned_phone_number ? (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Currently Configured</p>
                      <p className="text-sm font-mono text-green-700">{selectedAgent.assigned_phone_number}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => {
                      if (selectedAgent) {
                        removePhoneMutation.mutate(selectedAgent.agent_key)
                        setShowPhoneDialog(false)
                      }
                    }}
                    disabled={removePhoneMutation.isPending}
                    title={selectedAgent?.is_primary ? 'Will revert to main inbound line' : 'Remove phone number'}
                  >
                    {removePhoneMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <PhoneOff className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {selectedAgent?.is_primary && (
                  <p className="text-xs text-green-700 mt-2">
                    Note: Removing will revert to using the main inbound line
                  </p>
                )}
              </div>
            ) : selectedAgent?.is_primary ? (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Using Main Inbound Line</p>
                    <p className="text-xs text-blue-700">As primary agent, calls transfer back to the main line</p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Phone Number Input */}
            <div className="space-y-2">
              <Label htmlFor="phone_number">Bland.ai Inbound Number</Label>
              <Input
                id="phone_number"
                placeholder="+17145551234"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Enter an existing Bland.ai inbound number to assign to this agent
              </p>
            </div>

            {/* Configure Bland Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <Label>Configure Bland.ai</Label>
                <p className="text-sm text-muted-foreground">
                  Update the number with agent's voice, language & prompt
                </p>
              </div>
              <Switch
                checked={configureBland}
                onCheckedChange={setConfigureBland}
              />
            </div>

            {configureBland && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                <p className="font-medium text-blue-800 mb-1">Will configure:</p>
                <ul className="text-blue-700 space-y-0.5 ml-4 list-disc">
                  <li>Voice: {selectedAgent?.voice_name || selectedAgent?.voice_id || 'Default'}</li>
                  <li>Language: {selectedAgent?.primary_language_name || selectedAgent?.primary_language}</li>
                  <li>Language-specific prompt & greeting</li>
                  <li>Advisor tool for answering questions</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPhoneDialog(false)
              setSelectedAgent(null)
              setPhoneNumber('')
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePhone}
              disabled={configurePhoneMutation.isPending || !phoneNumber.trim()}
            >
              {configurePhoneMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {selectedAgent?.assigned_phone_number ? 'Update' : 'Configure'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AgentPool
